// frontend/src/components/HobbliShop.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import "./hobblishop.css";
import { formatCurrency } from "../../services/formatters";

const PAGE_SIZE = 48;

const firstImage = (listing) => {
  const images = Array.isArray(listing?.images) ? listing.images : [];
  const primary = images.find((image) => image?.is_primary) || images[0];
  return primary?.image_url || null;
};

export default function HobbliShop({ currentUser, sessionChecked }) {
  const navigate = useNavigate();

  const [itemsForSale, setItemsForSale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const requestVersion = useRef(0);
  const loadMoreBusy = useRef(false);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [searchDraft, setSearchDraft] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [year, setYear] = useState("");
  const [brand, setBrand] = useState("");
  const [setName, setSetName] = useState("");
  const [condition, setCondition] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id,name")
        .eq("allowed_in_shop", true)
        .order("name", { ascending: true });

      if (error) {
        console.error("Failed to load marketplace categories:", error);
        return;
      }
      setCategories(data || []);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    setSelectedSubcategory("");

    if (!selectedCategory) {
      setSubcategories([]);
      return;
    }

    const fetchSubcategories = async () => {
      const { data, error } = await supabase
        .from("subcategories")
        .select("id,name")
        .eq("category_id", selectedCategory)
        .eq("allowed_in_shop", true)
        .order("name", { ascending: true });

      if (error) {
        console.error("Failed to load marketplace subcategories:", error);
        setSubcategories([]);
        return;
      }
      setSubcategories(data || []);
    };

    fetchSubcategories();
  }, [selectedCategory]);

  const searchParams = useMemo(() => {
    const parsedYear = year ? Number(year) : null;
    const parsedMin = minPrice !== "" ? Number(minPrice) : null;
    const parsedMax = maxPrice !== "" ? Number(maxPrice) : null;
    return {
      p_search_text: searchText || null,
      p_shop_id: null,
      p_category_id: selectedCategory || null,
      p_subcategory_id: selectedSubcategory || null,
      p_year: Number.isFinite(parsedYear) ? parsedYear : null,
      p_brand: brand.trim() || null,
      p_set_name: setName.trim() || null,
      p_condition: condition.trim() || null,
      p_min_price: Number.isFinite(parsedMin) ? parsedMin : null,
      p_max_price: Number.isFinite(parsedMax) ? parsedMax : null,
      p_sort: sort,
    };
  }, [searchText, selectedCategory, selectedSubcategory, year, brand, setName,
      condition, minPrice, maxPrice, sort]);

  // New searches invalidate in-flight requests, including previous Load More calls.
  useEffect(() => {
    const version = ++requestVersion.current;
    loadMoreBusy.current = false;
    setLoading(true);
    setLoadingMore(false);
    setLoadError("");
    setItemsForSale([]);
    setTotalCount(0);
    setHasMore(false);

    const fetchFirstPage = async () => {
      try {
        const { data, error } = await supabase.rpc("search_marketplace", {
          ...searchParams, p_limit: PAGE_SIZE, p_offset: 0,
        });
        if (version !== requestVersion.current) return;
        if (error) throw error;
        const rows = Array.isArray(data) ? data : [];
        const count = Number(rows[0]?.total_count ?? rows.length);
        setItemsForSale(rows);
        setTotalCount(count);
        setHasMore(rows.length === PAGE_SIZE && rows.length < count);
      } catch (err) {
        if (version !== requestVersion.current) return;
        console.error("Marketplace search error:", err);
        setLoadError(err?.message || "Unable to load the marketplace.");
      } finally {
        if (version === requestVersion.current) setLoading(false);
      }
    };
    fetchFirstPage();
    return () => { requestVersion.current++; };
  }, [searchParams]);

  const loadMore = async () => {
    if (loading || loadingMore || loadMoreBusy.current || !hasMore) return;
    loadMoreBusy.current = true;
    const version = requestVersion.current;
    const offset = itemsForSale.length;
    setLoadingMore(true);
    setLoadError("");
    try {
      const { data, error } = await supabase.rpc("search_marketplace", {
        ...searchParams, p_limit: PAGE_SIZE, p_offset: offset,
      });
      if (version !== requestVersion.current) return;
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const seen = new Set(itemsForSale.map((row) => String(row.listing_id)));
      const additions = rows.filter((row) => {
        const id = String(row.listing_id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const count = Number(rows[0]?.total_count ?? totalCount);
      const nextLoaded = itemsForSale.length + additions.length;
      setItemsForSale((previous) => [...previous, ...additions]);
      setTotalCount(count);
      // Offset pagination may encounter changing inventory. Never loop forever
      // if a page contains no new records.
      setHasMore(rows.length === PAGE_SIZE && additions.length > 0 && nextLoaded < count);
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("Marketplace load more error:", err);
      setLoadError(err?.message || "Unable to load more listings. Please try again.");
    } finally {
      if (version === requestVersion.current) {
        loadMoreBusy.current = false;
        setLoadingMore(false);
      }
    }
  };

  const activeFilterCount = useMemo(
    () =>
      [
        selectedCategory,
        selectedSubcategory,
        year,
        brand,
        setName,
        condition,
        minPrice,
        maxPrice,
      ].filter(Boolean).length,
    [
      selectedCategory,
      selectedSubcategory,
      year,
      brand,
      setName,
      condition,
      minPrice,
      maxPrice,
    ]
  );

  const submitSearch = (event) => {
    event.preventDefault();
    setSearchText(searchDraft.trim());
  };

  const clearAll = () => {
    setSearchDraft("");
    setSearchText("");
    setSelectedCategory("");
    setSelectedSubcategory("");
    setYear("");
    setBrand("");
    setSetName("");
    setCondition("");
    setMinPrice("");
    setMaxPrice("");
    setSort("newest");
  };

  const goToSeller = (event, item) => {
    event.stopPropagation();
    if (item?.shop_slug) navigate(`/${item.shop_slug}`);
  };

  return (
    <div className="hobblishop-container">
      <section className="marketplace-hero">
        <div className="marketplace-hero-glow marketplace-hero-glow--one" />
        <div className="marketplace-hero-glow marketplace-hero-glow--two" />

        <div className="marketplace-hero-content">
          <div className="marketplace-logo-placeholder" aria-hidden="true">
            <span>H</span>
          </div>

          <div className="marketplace-hero-copy">
            <div className="shop-eyebrow">Hobbli Marketplace</div>
            <h1 className="shop-title">Find your next collectible.</h1>
            <p className="shop-subtitle">
              Shop collectibles from the Hobbli community and Hobbli Official.
            </p>
          </div>
          <button className="marketplace-cart-shortcut" type="button" onClick={() => navigate("/cart")}>
            Cart
          </button>
        </div>
      </section>

      {sessionChecked && currentUser && (
        <div className="marketplace-seller-cta">
          <div>
            <strong>Have collectibles to sell?</strong>
            <span>Open your own Hobbli shop.</span>
          </div>
          <button type="button" onClick={() => navigate("/sell")}>Sell on Hobbli</button>
        </div>
      )}

      {sessionChecked && !currentUser && (
        <div className="shop-cta">
          <div className="shop-cta-copy">
            <strong>Build your collection on Hobbli</strong>
            <span>Track collectibles, values, and your collection for free.</span>
          </div>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={() => navigate("/login")}>
              Create Account
            </button>
            <button className="cta-secondary" onClick={() => navigate("/login")}>
              Sign In
            </button>
          </div>
        </div>
      )}

      <section className="marketplace-controls">
        <form className="marketplace-search-row" onSubmit={submitSearch}>
          <div className="marketplace-search-shell">
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Search all Hobbli — name, brand, set, keyword..."
              aria-label="Search Hobbli Marketplace"
            />
            {searchDraft && (
              <button
                type="button"
                className="marketplace-search-clear"
                onClick={() => {
                  setSearchDraft("");
                  if (searchText) setSearchText("");
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <button className="marketplace-search-button" type="submit">
            Search Marketplace
          </button>

          <label className="marketplace-sort">
            <span>Sort</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </label>
        </form>

        <div className="marketplace-filter-row">
          <select
            aria-label="Filter by category"
            value={selectedCategory}
            onChange={(event) => setSelectedCategory(event.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Filter by subcategory"
            value={selectedSubcategory}
            onChange={(event) => setSelectedSubcategory(event.target.value)}
            disabled={!selectedCategory}
          >
            <option value="">All Subcategories</option>
            {subcategories.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            className={`marketplace-filter-toggle ${
              showMoreFilters ? "is-open" : ""
            }`}
            onClick={() => setShowMoreFilters((value) => !value)}
          >
            More Filters{activeFilterCount > 2 ? ` (${activeFilterCount - 2})` : ""}
          </button>

          {(activeFilterCount > 0 || searchText) && (
            <button type="button" className="shop-clear-filter" onClick={clearAll}>
              Clear all
            </button>
          )}

          <div className="shop-results-count">
            {loading ? "Searching…" : `${totalCount} ${Number(totalCount) === 1 ? "listing" : "listings"}`}
          </div>
        </div>

        {showMoreFilters && (
          <div className="marketplace-more-filters">
            <label>
              <span>Year</span>
              <input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 2024"
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </label>

            <label>
              <span>Brand</span>
              <input
                placeholder="e.g. Topps"
                value={brand}
                onChange={(event) => setBrand(event.target.value)}
              />
            </label>

            <label>
              <span>Set</span>
              <input
                placeholder="e.g. Chrome"
                value={setName}
                onChange={(event) => setSetName(event.target.value)}
              />
            </label>

            <label>
              <span>Condition</span>
              <input
                placeholder="e.g. Near Mint"
                value={condition}
                onChange={(event) => setCondition(event.target.value)}
              />
            </label>

            <label>
              <span>Min price</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="$0"
                value={minPrice}
                onChange={(event) => setMinPrice(event.target.value)}
              />
            </label>

            <label>
              <span>Max price</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Any"
                value={maxPrice}
                onChange={(event) => setMaxPrice(event.target.value)}
              />
            </label>
          </div>
        )}
      </section>

      <section className="marketplace-inventory">
        <div className="marketplace-section-heading">
          <div>
            <div className="shop-eyebrow">
              {searchText ? "Marketplace search" : "Hobbli Marketplace"}
            </div>
            <h2>
              {searchText
                ? `${totalCount} result${Number(totalCount) === 1 ? "" : "s"} for “${searchText}”`
                : "Available across Hobbli"}
            </h2>
          </div>
        </div>

        {loadError && <div className="marketplace-error">{loadError}</div>}

        {loading ? (
          <div className="shop-loading">
            <div className="shop-loading-spinner" />
            <span>Loading marketplace...</span>
          </div>
        ) : itemsForSale.length === 0 ? (
          <div className="empty-state">
            <h2>No matches</h2>
            <p>Try another search or adjust your marketplace filters.</p>
            {(activeFilterCount > 0 || searchText) && (
              <button type="button" className="shop-clear-filter" onClick={clearAll}>
                Clear Search & Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="shop-grid">
              {itemsForSale.map((item) => {
                const imageUrl = firstImage(item);
                const meta = [item.year, item.brand, item.set_name, item.subset,
                  item.card_number ? `#${item.card_number}` : null].filter(Boolean);
                return (
                  <article key={item.listing_id} className="marketplace-card"
                    onClick={() => navigate(`/marketplace/${item.listing_id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/marketplace/${item.listing_id}`);
                      }
                    }} role="button" tabIndex={0}>
                    <div className="marketplace-card-image-shell">
                      {imageUrl ? <img src={imageUrl} alt={item.title || "Marketplace listing"}
                        className="marketplace-card-image" loading="lazy" /> :
                        <div className="marketplace-card-no-image">No image</div>}
                      {item.is_platform_shop && <div className="marketplace-official-badge">Hobbli Official</div>}
                      {item.accepts_offers && <div className="marketplace-offer-badge">Offers</div>}
                    </div>
                    <div className="marketplace-card-body">
                      <div className="marketplace-card-meta">{meta.join(" · ")}</div>
                      <h2 className="marketplace-card-title">{item.title}</h2>
                      <div className="marketplace-card-price">{formatCurrency(item.price_amount)}</div>
                      <div className="marketplace-card-footer">
                        <button type="button"
                          className={`marketplace-shop-link ${item.is_platform_shop ? "is-official" : ""}`}
                          onClick={(event) => goToSeller(event, item)}>
                          <span className="marketplace-shop-avatar">{(item.shop_name || "H").charAt(0).toUpperCase()}</span>
                          <span className="marketplace-shop-copy">
                            <small>{item.is_platform_shop ? "Official shop" : "Shop"}</small>
                            <strong>{item.shop_name}</strong>
                          </span>
                        </button>
                        {item.quantity_available > 1 &&
                          <span className="marketplace-quantity">{item.quantity_available} available</span>}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="marketplace-pagination" aria-live="polite">
              <span>Showing {itemsForSale.length} of {totalCount} listings</span>
              {hasMore && (
                <button type="button" className="marketplace-load-more"
                  onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading more…" : "Load More Listings"}
                </button>
              )}
              {loadError && <span className="marketplace-pagination-error">{loadError}</span>}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
